/**
 * Solution pages over published pipelines (ADR-0032).
 *
 * Every role may read these: a page shows the *results* of runs of pipelines
 * a Developer published to it — never a pipeline's configuration or canvas.
 */

import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";

export type SolutionPageOut = components["schemas"]["SolutionPageOut"];
export type SolutionPageListOut = components["schemas"]["SolutionPageListOut"];
export type PublishedPipelineOut = components["schemas"]["PublishedPipelineOut"];
export type PublishedPipelineListOut = components["schemas"]["PublishedPipelineListOut"];

export function listSolutionPages(token: string): Promise<SolutionPageListOut> {
  return apiFetch<SolutionPageListOut>("/api/v1/solution-pages", { token });
}

export function listPublishedPipelines(
  token: string,
  page: string,
): Promise<PublishedPipelineListOut> {
  return apiFetch<PublishedPipelineListOut>(
    `/api/v1/solution-pages/${encodeURIComponent(page)}/pipelines`,
    { token },
  );
}

export type ResultsSummaryOut = components["schemas"]["ResultsSummaryOut"];
export type TrendBucketOut = components["schemas"]["TrendBucketOut"];
export type ResultsRunOut = components["schemas"]["ResultsRunOut"];
export type ResultsRunListOut = components["schemas"]["ResultsRunListOut"];
export type ResultsRunDetailOut = components["schemas"]["ResultsRunDetailOut"];
export type DocumentOutcomeOut = components["schemas"]["DocumentOutcomeOut"];

export interface ResultsFilter {
  days: number;
  pipelineId?: string | null;
  status?: string | null;
}

function query(f: ResultsFilter, withStatus: boolean): string {
  const q = new URLSearchParams({ days: String(f.days) });
  if (f.pipelineId) q.set("pipeline_id", f.pipelineId);
  if (withStatus && f.status) q.set("status", f.status);
  return q.toString();
}

const base = (page: string) => `/api/v1/solution-pages/${encodeURIComponent(page)}`;

export function getResultsSummary(
  token: string,
  page: string,
  f: ResultsFilter,
): Promise<ResultsSummaryOut> {
  return apiFetch<ResultsSummaryOut>(`${base(page)}/summary?${query(f, false)}`, { token });
}

export function listResultsRuns(
  token: string,
  page: string,
  f: ResultsFilter,
): Promise<ResultsRunListOut> {
  return apiFetch<ResultsRunListOut>(`${base(page)}/runs?${query(f, true)}`, { token });
}

export function getResultsRun(
  token: string,
  page: string,
  runId: string,
): Promise<ResultsRunDetailOut> {
  return apiFetch<ResultsRunDetailOut>(`${base(page)}/runs/${encodeURIComponent(runId)}`, {
    token,
  });
}

/** The per-document table as CSV. Audited server-side (`solution_results.exported`). */
export function resultsRunCsvPath(page: string, runId: string): string {
  return `${base(page)}/runs/${encodeURIComponent(runId)}/documents.csv`;
}
